'use client';

import { useAuth } from '@/hooks/useAuth';
import { Alert } from '@/components/common/Alert';
import { LoadingState } from '@/components/common/LoadingState';
import type { Tournament } from '@/lib/types/auction';
import { useRouter } from 'next/navigation';
import { useEffect } from 'react';

interface TournamentAuthGuardProps {
  tournament: Tournament;
  children: React.ReactNode;
  allowedStatuses?: Tournament['status'][];
  requireOwner?: boolean;
}

export function TournamentAuthGuard({
  tournament,
  children,
  allowedStatuses,
  requireOwner = true,
}: TournamentAuthGuardProps) {
  const { user, isAuthenticated } = useAuth();
  const router = useRouter();

  useEffect(() => {
    if (!isAuthenticated) {
      router.replace('/auth/login');
    }
  }, [isAuthenticated, router]);

  if (!isAuthenticated) {
    return <LoadingState message="Checking authorization..." />;
  }

  // Check tournament ownership if required
  if (requireOwner && tournament?.owner?.id !== user?.id) {
    return (
      <Alert
        type="error"
        message="You do not have permission to access this tournament."
      />
    );
  }

  // Check tournament status if specified
  if (allowedStatuses && !allowedStatuses.includes(tournament?.status)) {
    return (
      <Alert
        type="error"
        message={`This action is not allowed for tournaments in ${tournament.status.toLowerCase()} status.`}
      />
    );
  }

  return <>{children}</>;
}
